/**
=========================================================
* Material Dashboard 2 PRO React TS - v1.0.2
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-2-pro-react-ts
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { sky, lime, red, stone, yellow } from '@/assets/md-theme.tailwind';

/**
 * The base colors for the Material Dashboard 2 PRO React TSUI Dashboard PRO Material.
 * You can add new color using this file.
 * You can customized the colors for the entire Material Dashboard 2 PRO React TSUI Dashboard PRO Material using thie file.
 */

// types
interface ColorsTypes {
  main: string;
  focus: string;
}

interface GradientsTypes {
  main: string;
  state: string;
}

interface SocialMediaColorsTypes {
  main: string;
  dark: string;
}

interface BadgeColorsTypes {
  background: string;
  text: string;
}

interface Types {
  background:
    | {
        default: string;
        sidenav?: string;
        card?: string;
      }
    | any;
  white:
    | {
        main: string;
        focus: string;
      }
    | any;
  text:
    | {
        main: string;
        focus: string;
        primary?: string;
        secondary?: string;
        disabled?: string;
      }
    | any;
  transparent:
    | {
        main: string;
      }
    | any;
  black:
    | {
        light: string;
        main: string;
        focus: string;
      }
    | any;
  primary: ColorsTypes | any;
  secondary: ColorsTypes | any;
  info: ColorsTypes | any;
  success: ColorsTypes | any;
  warning: ColorsTypes | any;
  error: ColorsTypes | any;
  light: ColorsTypes | any;
  dark: ColorsTypes | any;
  grey: {
    [key: number]: string;
  };
  gradients:
    | {
        primary: GradientsTypes;
        secondary: GradientsTypes;
        info: GradientsTypes;
        success: GradientsTypes;
        warning: GradientsTypes;
        error: GradientsTypes;
        light: GradientsTypes;
        dark: GradientsTypes;
      }
    | any;
  socialMediaColors:
    | {
        facebook: SocialMediaColorsTypes;
        twitter: SocialMediaColorsTypes;
        instagram: SocialMediaColorsTypes;
        linkedin: SocialMediaColorsTypes;
        pinterest: SocialMediaColorsTypes;
        youtube: SocialMediaColorsTypes;
        vimeo: SocialMediaColorsTypes;
        slack: SocialMediaColorsTypes;
        dribbble: SocialMediaColorsTypes;
        github: SocialMediaColorsTypes;
        reddit: SocialMediaColorsTypes;
        tumblr: SocialMediaColorsTypes;
      }
    | any;
  badgeColors:
    | {
        primary: BadgeColorsTypes;
        secondary: BadgeColorsTypes;
        info: BadgeColorsTypes;
        success: BadgeColorsTypes;
        warning: BadgeColorsTypes;
        error: BadgeColorsTypes;
        light: BadgeColorsTypes;
        dark: BadgeColorsTypes;
      }
    | any;
  coloredShadows:
    | {
        [key: string]: string;
      }
    | any;
  inputBorderColor: string;
  tabs:
    | {
        indicator:
          | {
              boxShadow: string;
            }
          | any;
      }
    | any;
}

const colors: Types = {
  background: {
    default: stone[50],
    sidenav: stone[100],
  },

  text: {
    main: stone[800],
    focus: '#7b809a',
  },

  transparent: {
    main: 'rgba(0, 0, 0, 0)',
  },

  white: {
    main: '#ffffff',
    focus: '#ffffff',
  },

  black: {
    light: '#000000',
    main: '#000000',
    focus: '#000000',
  },

  primary: {
    main: stone[900],
    focus: stone[800],
  },

  secondary: {
    main: stone[600],
    focus: stone[500],
  },

  info: {
    main: sky[500],
    focus: sky[600],
  },

  success: {
    main: lime[500],
    focus: lime[600],
  },

  warning: {
    main: yellow[500],
    focus: yellow[600],
  },

  error: {
    main: red[500],
    focus: red[600],
  },

  light: {
    main: stone[200],
    focus: stone[100],
  },

  dark: {
    main: stone[900],
    focus: stone[800],
  },

  grey: stone,

  gradients: {
    primary: {
      main: red[500],
      state: red[600],
    },

    secondary: {
      main: stone[600],
      state: stone[500],
    },

    info: {
      main: sky[500],
      state: sky[600],
    },

    success: {
      main: lime[500],
      state: lime[600],
    },

    warning: {
      main: yellow[500],
      state: yellow[600],
    },

    error: {
      main: red[500],
      state: red[600],
    },

    light: {
      main: stone[200],
      state: stone[100],
    },

    dark: {
      main: stone[900],
      state: stone[800],
    },
  },

  socialMediaColors: {
    facebook: {
      main: '#3b5998',
      dark: '#344e86',
    },

    twitter: {
      main: '#55acee',
      dark: '#3ea1ec',
    },

    instagram: {
      main: '#125688',
      dark: '#0e456d',
    },

    linkedin: {
      main: '#0077b5',
      dark: '#00669c',
    },

    pinterest: {
      main: '#cc2127',
      dark: '#b21d22',
    },

    youtube: {
      main: '#e52d27',
      dark: '#d41f1a',
    },

    vimeo: {
      main: '#1ab7ea',
      dark: '#13a3d2',
    },

    slack: {
      main: '#3aaf85',
      dark: '#329874',
    },

    dribbble: {
      main: '#ea4c89',
      dark: '#e73177',
    },

    github: {
      main: '#24292e',
      dark: '#171a1d',
    },

    reddit: {
      main: '#ff4500',
      dark: '#e03d00',
    },

    tumblr: {
      main: '#35465c',
      dark: '#2a3749',
    },
  },

  badgeColors: {
    primary: {
      background: stone[200],
      text: stone[500],
    },

    secondary: {
      background: stone[300],
      text: stone[50],
    },

    info: {
      background: sky[200],
      text: sky[500],
    },

    success: {
      background: lime[200],
      text: lime[500],
    },

    warning: {
      background: yellow[200],
      text: yellow[500],
    },

    error: {
      background: red[200],
      text: red[500],
    },

    light: {
      background: stone[100],
      text: stone[500],
    },

    dark: {
      background: stone[900],
      text: stone[500],
    },
  },

  coloredShadows: {
    primary: red[500],
    secondary: stone[900],
    info: sky[500],
    success: lime[500],
    warning: yellow[500],
    error: red[500],
    light: stone[200],
    dark: stone[900],
  },

  inputBorderColor: stone[300],

  tabs: {
    indicator: { boxShadow: '#ddd' },
  },
};

export default colors;
